import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../components/layout/Sidebar";
import { AUTH_STORAGE_KEY } from "../socket";

const getToken = () => {
  try {
    return JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY))?.token || null;
  } catch {
    return null;
  }
};

const authHeader = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function FieldMasterPage() {
  const [role, setRole] = useState(3);
  const [fields, setFields] = useState([]);
  const [form, setForm] = useState({ name: "", description: "" });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const isAdmin = useMemo(() => Number(role) === 1 || Number(role) === 2, [role]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    window.setTimeout(() => setToast(null), 2500);
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      const user = parsed?.user || {};
      setRole(Number(user.role) || (user.isAdmin ? 2 : 3));
    } catch {
      setRole(3);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 640);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const loadFields = async () => {
      try {
        const res = await fetch("/api/fields", { headers: authHeader() });
        if (!res.ok) {
          if (res.status === 403) {
            showToast("Only admins can view fields.", "error");
          }
          setFields([]);
          return;
        }
        const data = await res.json();
        setFields(Array.isArray(data.fields) ? data.fields : []);
      } catch {
        setFields([]);
      }
    };

    if (isAdmin) loadFields();
  }, [isAdmin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const description = form.description.trim();

    if (!name) {
      showToast("Field name is required.", "error");
      return;
    }

    setSaving(true);
    try {
      const url = editingId ? `/api/fields/${editingId}` : "/api/fields";
      const method = editingId ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ name, description }),
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || "Failed to save field.", "error");
      } else {
        const fresh = data.field;
        if (editingId) {
          setFields((prev) => prev.map((item) => (item.id === editingId ? fresh : item)));
          showToast("Field updated.");
        } else {
          setFields((prev) => [fresh, ...prev]);
          showToast("Field added.");
        }
        setForm({ name: "", description: "" });
        setEditingId(null);
      }
    } catch {
      showToast("Failed to save field.", "error");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({ name: item.name || "", description: item.description || "" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ name: "", description: "" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this field?")) return;
    try {
      const res = await fetch(`/api/fields/${id}`, { method: "DELETE", headers: authHeader() });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || "Delete failed.", "error");
      } else {
        setFields((prev) => prev.filter((item) => item.id !== id));
        if (editingId === id) cancelEdit();
        showToast("Field deleted.");
      }
    } catch {
      showToast("Delete failed.", "error");
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f8fafc" }}>
      {!isMobile && <Sidebar role={role} />}

      <div style={{ flex: 1, padding: isMobile ? 16 : 24, overflow: "auto" }}>
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          <div style={{ marginBottom: 20 }}>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Field Master</h2>
            <p style={{ margin: "6px 0 0", color: "#64748b" }}>
              Add, update, and remove field entries. This page is for admins only.
            </p>
          </div>

          {!isAdmin ? (
            <div style={{ background: "#fff1f2", border: "1px solid #fecdd3", color: "#be123c", padding: 16, borderRadius: 10 }}>
              Access denied. Only admins can manage fields.
            </div>
          ) : (
            <>
              <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: 16, marginBottom: 18, boxShadow: "0 2px 8px rgba(15,23,42,0.04)" }}>
                {toast && (
                  <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: toast.type === "success" ? "#ecfeff" : "#fef2f2", color: toast.type === "success" ? "#0f766e" : "#b91c1c", fontWeight: 600 }}>
                    {toast.msg}
                  </div>
                )}

                <form onSubmit={handleSubmit} style={{ display: "grid", gap: 12 }}>
                  <div style={{ display: "grid", gap: 8 }}>
                    <label style={{ fontWeight: 600, color: "#334155" }}>Field Name</label>
                    <input
                      value={form.name}
                      onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter field name"
                      style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid #cbd5e1" }}
                    />
                  </div>

                  <div style={{ display: "grid", gap: 8 }}>
                    <label style={{ fontWeight: 600, color: "#334155" }}>Description</label>
                    <textarea
                      value={form.description}
                      onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                      placeholder="Optional description"
                      rows={3}
                      style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid #cbd5e1", resize: "vertical" }}
                    />
                  </div>

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button type="submit" disabled={saving} style={{ padding: "10px 16px", border: "none", borderRadius: 8, background: "#2563eb", color: "#fff", cursor: saving ? "not-allowed" : "pointer", fontWeight: 700 }}>
                      {saving ? "Saving..." : editingId ? "Update Field" : "Add Field"}
                    </button>
                    {editingId && (
                      <button type="button" onClick={cancelEdit} style={{ padding: "10px 16px", border: "1px solid #cbd5e1", borderRadius: 8, background: "#fff", cursor: "pointer", fontWeight: 700 }}>
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>

              <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 8px rgba(15,23,42,0.04)" }}>
                <div style={{ padding: "14px 16px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
                  <strong>Existing Fields</strong>
                </div>

                {fields.length === 0 ? (
                  <div style={{ padding: 20, color: "#64748b" }}>No fields yet. Add one to get started.</div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: "#f8fafc", textAlign: "left" }}>
                          <th style={{ padding: 12, borderBottom: "1px solid #e2e8f0" }}>Name</th>
                          <th style={{ padding: 12, borderBottom: "1px solid #e2e8f0" }}>Description</th>
                          <th style={{ padding: 12, borderBottom: "1px solid #e2e8f0" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fields.map((item) => (
                          <tr key={item.id}>
                            <td style={{ padding: 12, borderBottom: "1px solid #e2e8f0" }}>{item.name}</td>
                            <td style={{ padding: 12, borderBottom: "1px solid #e2e8f0" }}>{item.description || "—"}</td>
                            <td style={{ padding: 12, borderBottom: "1px solid #e2e8f0" }}>
                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <button type="button" onClick={() => startEdit(item)} style={{ padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 6, background: "#fff", cursor: "pointer" }}>
                                  Edit
                                </button>
                                <button type="button" onClick={() => handleDelete(item.id)} style={{ padding: "7px 10px", border: "1px solid #fecaca", borderRadius: 6, background: "#fef2f2", color: "#b91c1c", cursor: "pointer" }}>
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
